import React, { useEffect, useState } from 'react';

import { createStyles, Paper, Text } from '@mantine/core';
import PieChart, { ChartData } from 'components/charts/PieChart';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import usePackageRevenue from 'hooks/reports/revenue/usePackageRevenue';
import { CellProps } from 'react-table';
import { PackageRevenueReport } from 'types';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  chart: {
    paddingTop: theme.spacing.md,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
  },
  totalsRow: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    td: {
      padding: theme.spacing.sm,
    },
  },
}));

const PackageRevenueTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const { classes } = useStyles();
  const { formatCurrency } = useLocalization();

  const [ordering, setOrdering] = useState('revenue');
  const [ascending, setAscending] = useState(false);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [tableData, setTableData] = useState<PackageRevenueReport[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePackageRevenue({
      dateStart: searchFilters.dateRange[0] ? searchFilters.dateRange[0].toISOString() : undefined,
      dateEnd: searchFilters.dateRange[1] ? searchFilters.dateRange[1].toISOString() : undefined,
      ordering: ascending ? ordering : `-${ordering}`,
      limit: '100',
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  useEffect(() => {
    if (!isLoading && data) {
      const flatData = data.pages
        .map(page => page.data.results)
        .flat()
        .map(p => ({ label: p.package, value: p.revenue / 100 }));

      if (flatData.length > 5) {
        const top5 = flatData.slice(0, 5);
        const allOtherSum = flatData.slice(5).reduce((prev, curr) => prev + curr.value, 0);
        setChartData([...top5, { label: 'All Others', value: allOtherSum }]);
      } else {
        setChartData(flatData);
      }
      setChartData(
        data.pages
          .map(page => page.data.results)
          .flat()
          .map(p => ({ label: p.package, value: p.revenue / 100 })),
      );
    }
  }, [isLoading, data, setChartData]);

  const columns = [
    {
      Header: 'Package',
      accessor: 'package' as const,
    },
    {
      Header: 'Archived',
      accessor: 'deletedAt' as const,
      Cell: ({ value }: CellProps<PackageRevenueReport>) => (!!value ? 'Archived' : ''),
    },
    {
      Header: 'Athletes',
      accessor: 'athletes' as const,
      Cell: ({ value }: CellProps<PackageRevenueReport>) => value?.toString() || '0',
    },
    {
      Header: 'Sold Packages',
      accessor: 'athletePackages' as const,
      Cell: ({ value }: CellProps<PackageRevenueReport>) => value?.toString() || '0',
    },
    {
      Header: 'Invoices',
      accessor: 'invoices' as const,
      Cell: ({ value }: CellProps<PackageRevenueReport>) => value?.toString() || '0',
    },
    {
      Header: 'Revenue',
      accessor: 'revenue' as const,
      Cell: ({ value }: CellProps<PackageRevenueReport>) => formatCurrency(value / 100),
    },
  ];

  const totals = data?.pages[0]?.data?.totals;

  const TotalsFooter = (): JSX.Element => (
    <tr className={classes.totalsRow}>
      <td>
        <Text weight={600}>Total</Text>
      </td>
      <td></td>
      <td>
        <Text weight={600}>{totals?.athletes}</Text>
      </td>
      <td>
        <Text weight={600}>{totals?.athletePackages}</Text>
      </td>
      <td>
        <Text weight={600}>{totals?.invoices}</Text>
      </td>
      <td>
        <Text weight={600}>{formatCurrency((totals?.revenue ?? 0) / 100)}</Text>
      </td>
    </tr>
  );

  return (
    <div className={classes.content}>
      <div>
        <Table
          tableData={tableData}
          columns={columns}
          isSuccess={isSuccess}
          isLoading={isLoading}
          isError={isError}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          fetchNextPage={fetchNextPage}
          clickableHeaders={['package', 'athletes', 'athletePackages', 'invoices', 'revenue']}
          handleHeaderClick={handleHeaderClick}
          ordering={ordering}
          ascending={ascending}
          renderFooter={() => (totals ? <TotalsFooter /> : null)}
        />
      </div>
      {isSuccess && chartData.length > 0 && (
        <div className={classes.chart}>
          <Paper withBorder shadow='md' radius='md'>
            <PieChart data={chartData} />
          </Paper>
        </div>
      )}
    </div>
  );
};

export default PackageRevenueTable;
